import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'

export interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

interface CookieConsentContextValue {
  preferences: CookiePreferences
  hasConsented: boolean
  showBanner: boolean
  setShowBanner: (show: boolean) => void
  acceptAll: () => void
  rejectAll: () => void
  savePreferences: (prefs: CookiePreferences) => void
}

const STORAGE_KEY = 'pel-cookie-consent'
const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null)

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences)
  const [hasConsented, setHasConsented] = useState(false)
  const [showBanner, setShowBanner] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CookiePreferences
        setPreferences({ ...parsed, necessary: true })
        setHasConsented(true)
      } catch {
        setShowBanner(true)
      }
    } else {
      setShowBanner(true)
    }
  }, [])

  useEffect(() => {
    const handleOpen = () => setShowBanner(true)
    window.addEventListener('open-cookie-preferences', handleOpen)
    return () => window.removeEventListener('open-cookie-preferences', handleOpen)
  }, [])

  const save = useCallback((prefs: CookiePreferences) => {
    const safe = { ...prefs, necessary: true }
    setPreferences(safe)
    setHasConsented(true)
    setShowBanner(false)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(safe))
  }, [])

  const acceptAll = useCallback(() => {
    save({ necessary: true, analytics: true, marketing: true })
  }, [save])

  const rejectAll = useCallback(() => {
    save({ necessary: true, analytics: false, marketing: false })
  }, [save])

  return (
    <CookieConsentContext.Provider
      value={{ preferences, hasConsented, showBanner, setShowBanner, acceptAll, rejectAll, savePreferences: save }}
    >
      {children}
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext)
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider')
  }
  return context
}
